import { WorkspaceFieldLabel } from '@components/Common/EntityWorkspace';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import { Button, Checkbox, FormControlLabel, IconButton } from '@mui/material';
import './index.scss';

export interface EntryField<T> {
  key: keyof T & string;
  label: string;
  type?: 'text' | 'number' | 'boolean';
  placeholder?: string;
}

export interface EntrySummary {
  title: string;
  subtitle?: string;
  meta?: string;
}

interface EntryListProps<T> {
  entries: T[];
  editing: boolean;
  fields: EntryField<T>[];
  onChange: (entries: T[]) => void;
  /** How one entry reads when not editing. */
  summary: (entry: T) => EntrySummary;
  /** A fresh, empty entry for "Add". */
  blank: () => T;
  addLabel: string;
  emptyLabel: string;
  /** Singular noun for screen readers: "Remove job 2". */
  itemLabel: string;
}

/**
 * A repeatable list of structured entries inside a workspace SectionCard: work history,
 * qualifications, certificates.
 *
 * Shared because three cards need exactly this. Layout lives in the stylesheet and every value
 * is a token, so the cards stay on the theme in both modes. (The older internal-fields row editor
 * on the job-seeker page is a separate, hand-rolled editor and is not built on this.)
 *
 * Numbers are cleared to null, not 0: an empty year means "not stated", and 0 would publish a
 * year nobody gave.
 */
function EntryList<T extends object>({
  entries,
  editing,
  fields,
  onChange,
  summary,
  blank,
  addLabel,
  emptyLabel,
  itemLabel,
}: EntryListProps<T>) {
  const update = (index: number, key: keyof T, value: unknown) =>
    onChange(entries.map((e, i) => (i === index ? { ...e, [key]: value } : e)));

  if (!editing) {
    if (entries.length === 0) {
      return <p className="cc-entries cc-entries__empty">{emptyLabel}</p>;
    }
    return (
      <ul className="cc-entries">
        {entries.map((entry, i) => {
          const s = summary(entry);
          return (
            <li key={i} className="cc-entries__item">
              <span className="cc-entries__title">{s.title}</span>
              {s.subtitle && <span className="cc-entries__subtitle">{s.subtitle}</span>}
              {s.meta && <span className="cc-entries__meta">{s.meta}</span>}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="cc-entries">
      {entries.map((entry, i) => (
        <fieldset key={i} className="cc-entries__edit">
          <legend className="cc-entries__legend">
            {itemLabel} {i + 1}
          </legend>
          <div className="cc-entries__grid">
            {fields.map((f) => {
              const value = entry[f.key] as unknown;
              if (f.type === 'boolean') {
                return (
                  <FormControlLabel
                    key={f.key}
                    className="cc-entries__check"
                    label={f.label}
                    control={(
                      <Checkbox
                        size="small"
                        checked={Boolean(value)}
                        onChange={(_e, checked) => update(i, f.key, checked)}
                      />
                    )}
                  />
                );
              }
              return (
                <label key={f.key} className="cc-entries__field">
                  <WorkspaceFieldLabel>{f.label}</WorkspaceFieldLabel>
                  <WorkspaceInput
                    type={f.type === 'number' ? 'number' : 'text'}
                    placeholder={f.placeholder}
                    value={(value as string | number | null | undefined) ?? ''}
                    onChange={(v) =>
                      update(i, f.key, f.type === 'number' ? (v === '' ? null : Number(v)) : v)
                    }
                  />
                </label>
              );
            })}
          </div>
          <IconButton
            className="cc-entries__remove"
            color="error"
            size="small"
            aria-label={`Remove ${itemLabel.toLowerCase()} ${i + 1}`}
            onClick={() => onChange(entries.filter((_, idx) => idx !== i))}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </fieldset>
      ))}
      <Button
        className="cc-entries__add"
        size="small"
        startIcon={<AddIcon />}
        onClick={() => onChange([...entries, blank()])}
      >
        {addLabel}
      </Button>
    </div>
  );
}

export default EntryList;
